import React, { useEffect, useRef, useState } from 'react';
import { Clock, MessageSquare, Send, Sparkles, Users, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { submitTrialExtensionRequest } from '../../api/trialExtensionApi';
import { showErrorToast, showSuccessToast } from '../../lib/toast';

const TEAM_SIZE_OPTIONS = [
  { value: '', label: 'Select team size (optional)' },
  { value: '1', label: 'Just me' },
  { value: '2-10', label: '2–10' },
  { value: '11-50', label: '11–50' },
  { value: '51-200', label: '51–200' },
  { value: '201+', label: '201+' },
];

const REQUEST_CONFIG = {
  trial_extension: {
    icon: Clock,
    badge: 'Trial extension',
    title: 'Request more time',
    subtitle: 'Tell us about your team so we can extend your pilot',
    useCaseLabel: 'What are you evaluating? *',
    useCasePlaceholder: 'Describe the workflows, data, or decisions you are testing with Manteo.',
    showTrialBanner: true,
    badgeClassName: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200',
  },
  pro: {
    icon: Sparkles,
    badge: 'Premium plan',
    title: 'Get Premium Plan',
    subtitle: 'Tell us about your data stack and the decisions you want to speed up',
    useCaseLabel: 'What would Premium unlock for you? *',
    useCasePlaceholder: 'e.g. live warehouse queries, reusable business logic, production workflows…',
    showTrialBanner: false,
    badgeClassName: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200',
  },
  team: {
    icon: Users,
    badge: 'Pilot for Organizations',
    title: 'Pilot Request',
    subtitle: 'Tell us how your organization would use a shared data context',
    useCaseLabel: 'What would your org use Manteo for? *',
    useCasePlaceholder: 'e.g. team workflows, who needs access, decisions you want to align on…',
    showTrialBanner: false,
    badgeClassName: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  },
  custom: {
    icon: MessageSquare,
    badge: 'Custom request',
    title: 'Custom Request',
    subtitle: 'Tell us what a standard plan does not cover for you',
    useCaseLabel: 'What do you need? *',
    useCasePlaceholder: 'e.g. integrations, security requirements, deployment, capacity…',
    showTrialBanner: false,
    badgeClassName: 'bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-200',
  },
};

const inputClassName =
  'w-full rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/50 px-3 py-2.5 text-xs font-semibold text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30';

const labelClassName =
  'block text-[9px] font-black uppercase tracking-widest text-gray-400 mb-1.5';

const ContactRequestModal = ({
  isOpen,
  requestType,
  onClose,
  userEmail,
  userFullName,
  planName,
  trialEndsAt,
}) => {
  const config = REQUEST_CONFIG[requestType] || REQUEST_CONFIG.pro;
  const BadgeIcon = config.icon;

  const [fullName, setFullName] = useState('');
  const [organization, setOrganization] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [organizationDomain, setOrganizationDomain] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [useCase, setUseCase] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setFullName(userFullName || '');
    setOrganization('');
    setJobTitle('');
    setOrganizationDomain('');
    setTeamSize('');
    setUseCase('');
  }, [isOpen, userFullName, requestType]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const frame = requestAnimationFrame(() => {
      firstInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen, requestType]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !isSubmitting) {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, isSubmitting]);

  const canSubmit =
    organization.trim() &&
    jobTitle.trim() &&
    organizationDomain.trim() &&
    useCase.trim() &&
    !isSubmitting;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || !requestType) return;

    setIsSubmitting(true);
    try {
      await submitTrialExtensionRequest({
        request_type: requestType,
        full_name: fullName.trim() || undefined,
        organization: organization.trim(),
        job_title: jobTitle.trim(),
        organization_domain: organizationDomain.trim(),
        team_size: teamSize || undefined,
        use_case: useCase.trim(),
      });
      showSuccessToast('Request sent — we will be in touch soon.');
      onClose();
    } catch {
      showErrorToast('Could not send your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !requestType) return null;

  const handleBackdropClick = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
        onClick={handleBackdropClick}
        aria-hidden
      />
      <div
        className="relative flex w-full max-w-lg max-h-[90vh] flex-col overflow-hidden rounded-[32px] border border-gray-100 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-950 animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-labelledby="contact-request-title"
        aria-modal="true"
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-50 p-6 dark:border-gray-900">
          <div>
            <div className={`mb-2 inline-flex items-center gap-2 rounded-lg px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${config.badgeClassName}`}>
              <BadgeIcon size={12} />
              {config.badge}
            </div>
            <h3 id="contact-request-title" className="text-xl font-black uppercase tracking-tighter text-gray-900 dark:text-white">
              {config.title}
            </h3>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
              {config.subtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 dark:hover:bg-gray-900 dark:hover:text-white"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-y-auto custom-scrollbar">
          <div className="space-y-4 p-6">
            {userEmail && (
              <div>
                <p className={labelClassName}>Work email</p>
                <p className="rounded-xl bg-gray-50 px-3 py-2.5 text-xs font-semibold text-gray-600 dark:bg-gray-900/50 dark:text-gray-300">
                  {userEmail}
                </p>
              </div>
            )}

            {config.showTrialBanner && (planName || trialEndsAt) && (
              <div className="rounded-2xl border border-amber-100 bg-amber-50/80 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-950/30">
                <p className="text-[9px] font-black uppercase tracking-widest text-amber-800 dark:text-amber-200">
                  Current trial
                </p>
                <p className="mt-1 text-xs font-semibold text-amber-950 dark:text-amber-100">
                  {planName ? `${planName} plan` : 'Trial'}
                  {trialEndsAt ? ` · ends ${trialEndsAt}` : ''}
                </p>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="contact-req-name" className={labelClassName}>Full name</label>
                <input
                  ref={firstInputRef}
                  id="contact-req-name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Smith"
                  maxLength={255}
                  className={inputClassName}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-req-org" className={labelClassName}>Organization *</label>
                <input
                  id="contact-req-org"
                  type="text"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder="Acme Corp"
                  maxLength={255}
                  required
                  className={inputClassName}
                />
              </div>
              <div>
                <label htmlFor="contact-req-title" className={labelClassName}>Job title *</label>
                <input
                  id="contact-req-title"
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="Head of Analytics"
                  maxLength={255}
                  required
                  className={inputClassName}
                />
              </div>
              <div>
                <label htmlFor="contact-req-domain" className={labelClassName}>Organization domain *</label>
                <input
                  id="contact-req-domain"
                  type="text"
                  value={organizationDomain}
                  onChange={(e) => setOrganizationDomain(e.target.value)}
                  placeholder="acme.com"
                  maxLength={255}
                  required
                  className={inputClassName}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-req-team" className={labelClassName}>Team size</label>
                <select
                  id="contact-req-team"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  className={inputClassName}
                >
                  {TEAM_SIZE_OPTIONS.map((opt) => (
                    <option key={opt.value || 'empty'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="contact-req-use-case" className={labelClassName}>{config.useCaseLabel}</label>
              <textarea
                id="contact-req-use-case"
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
                rows={3}
                maxLength={4000}
                required
                placeholder={config.useCasePlaceholder}
                className={cn(inputClassName, 'resize-none font-medium')}
              />
            </div>
          </div>

          <div className="shrink-0 border-t border-gray-50 bg-gray-50/50 p-4 dark:border-gray-900 dark:bg-gray-900/50">
            <button
              type="submit"
              disabled={!canSubmit}
              className={cn(
                'flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-[10px] font-black uppercase tracking-widest text-white transition-all',
                'bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/20',
                'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-emerald-600'
              )}
            >
              <Send size={14} />
              {isSubmitting ? 'Sending…' : 'Submit request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactRequestModal;
